// src/App.js
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom"; 
import { useEffect } from "react";

// 🔐 Context Providers
import { AuthProvider } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";

// ⬇️ Reusable UI Components
import BottomNav from "./components/BottomNav";

// ⬇️ Authentication Pages
import Login from "./pages/Login";
import Register from "./pages/Register";
import VerifyOtp from "./pages/VerifyOtp";
import CompleteRegister from "./pages/CompleteRegister";
import CategorySelection from "./pages/CategorySelection";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import ForgotFlow from "./components/ForgotFlow";

// ⬇️ Dashboards
import DashboardIndividual from "./pages/dashboards/Individual";
import DashboardBusiness from "./pages/dashboards/Business";
import DashboardVendor from "./pages/dashboards/Vendor";
import DashboardInstaller from "./pages/dashboards/Installer";
import AdminServiceApproval from "./pages/admin/AdminServiceApproval";


// ⬇️ Tools
import LoadCalculator from "./pages/LoadCalculator";
import PanelSizing from "./pages/PanelSizing";
import SolarSizingCalculator from "./pages/SolarSizingCalculator";
import BeginnerPanelSizing from "./pages/BeginnerPanelSizing";

// ⬇️ Pages
import Installers from "./pages/Installers";
import Vendors from "./pages/Vendors";
import Blogs from "./pages/Blogs";
import BlogList from "./components/BlogList";
import Resources from "./pages/Resources";
import SolarMarket from "./components/SolarMarket";
import SolarTools from "./components/SolarTools";
import SolarSizer from "./components/SolarSizer";
import SolarSeminar from "./components/SolarSeminar";
import VendorUploadService from "./pages/vendor/VendorUploadService";
import NotificationsPage from "./pages/NotificationsPage";
import ServiceCenters from "./pages/ServiceCenters";
import ApprovedVendors from "./pages/ApprovedVendors";
import ApprovedInstallers from "./pages/ApprovedInstallers";
import UploadService from "./pages/UploadService";
import UserLevelSelection from "./pages/UserLevelSelection";
import AutoRedirect from "./components/AutoRedirect";
import SolarQuiz from "./components/SolarQuiz/SolarQuiz";

// ✅ Error Code Components
import ErrorCodeSelector from "./components/ErrorCodeChecker/ErrorCodeSelector";
import ErrorCodeDisplay from "./components/ErrorCodeChecker/ErrorCodeDisplay";

// ⬇️ Reusable App Components
import Listings from "./components/Listings";
import Profiles from "./components/Profiles";
import Bookings from "./components/Bookings";
import Chat from "./components/Chat";
import Inbox from "./pages/Inbox";
import AISolarAssistant from "./components/AISolarAssistant";

// ✅ Legal Pages
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";

// ✅ Toastify
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ✅ Solar Resources
import SolarResourcesPage from "./components/SolarResources/SolarResourcesPage";
import SolarResourceDetail from "./components/SolarResources/SolarResourceDetail";

// ✅ Protected Route
import ProtectedRoute from "./components/ProtectedRoute";

// ✅ Paystack Payment callbacks
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed";

// ✅ NEPA/Disco Alerts page (new)
import NEPAAlerts from "./pages/NEPAAlerts";

// ===========================================
// 📦 Wrapper to render all routes + conditionally show bottom nav
// ===========================================
function AppWrapper() {
  const location = useLocation();


  const excludeNavRoutes = [
    "/", "/login", "/verify-otp", "/privacy-policy",
    "/terms-and-conditions", "/payment-success", "/payment-failed", "/forgot-password",
    "/reset-password/:token", "/solarsizer"
  ];

  const showBottomNav = !(
    excludeNavRoutes.includes(location.pathname) ||
    location.pathname.startsWith("/register") ||
    location.pathname.startsWith("/complete-registration")
  );

 

  return (
    <>

    {/* 🔥 Auto-redirect if user already logged in */}
      <AutoRedirect />
      <Routes>
        {/* ✅ Public Auth Routes */}
        <Route path="/" element={<CategorySelection />} />
        <Route path="/register/:category" element={<Register />} />
        <Route path="/verify-otp" element={<VerifyOtp />} />
        <Route path="/complete-registration" element={<CompleteRegister />} />
        <Route path="/login" element={<Login />} />
       
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotFlow />} />

        {/* ✅ Payment callbacks (public) */}
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/payment-failed" element={<PaymentFailed />} />

        {/* ✅ Protected Dashboards */}
        <Route path="/dashboard/individual" element={<ProtectedRoute><DashboardIndividual /></ProtectedRoute>} />
        <Route path="/dashboard/business" element={<ProtectedRoute><DashboardBusiness /></ProtectedRoute>} />
        <Route path="/dashboard/vendor" element={<ProtectedRoute><DashboardVendor /></ProtectedRoute>} />
        <Route path="/dashboard/installer" element={<ProtectedRoute><DashboardInstaller /></ProtectedRoute>} />
        <Route path="/admin/approve-services" element={<ProtectedRoute><AdminServiceApproval /></ProtectedRoute>} />


        {/* ✅ Tools */}
        <Route path="/calculator/load" element={<ProtectedRoute><LoadCalculator /></ProtectedRoute>} />
        <Route path="/calculator/panels" element={<ProtectedRoute><PanelSizing /></ProtectedRoute>} />
        <Route path="/calculator/sizing" element={<ProtectedRoute><SolarSizingCalculator /></ProtectedRoute>} />
        <Route path="/beginner-sizing" element={<ProtectedRoute><BeginnerPanelSizing/></ProtectedRoute>} />
        <Route path="/solartools" element={<ProtectedRoute><SolarTools/></ProtectedRoute>} />
        <Route path="/solarsizer" element={<ProtectedRoute><SolarSizer/></ProtectedRoute>} />

        {/* ✅ General Pages */}
        <Route path="/installers" element={<ProtectedRoute><Installers /></ProtectedRoute>} />
        <Route path="/vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />
        <Route path="/blogs" element={<ProtectedRoute><Blogs /></ProtectedRoute>} />
        <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
        <Route path="/installer/uploadService" element={<ProtectedRoute><UploadService /></ProtectedRoute>} />
        <Route path="/vendors/upload" element={<ProtectedRoute><VendorUploadService /></ProtectedRoute>} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/notifications/weather" element={<NotificationsPage filterType="system" />} />
        <Route path="/service-centers" element={<ProtectedRoute><ServiceCenters /></ProtectedRoute>} />
        <Route path="/approved-vendors" element={<ProtectedRoute><ApprovedVendors /></ProtectedRoute>} />
        <Route path="/approved-installers" element={<ProtectedRoute><ApprovedInstallers /></ProtectedRoute>} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/upload-service" element={<UploadService />} />
        <Route path="/select-level" element={<UserLevelSelection />} />
        <Route path="/solar-quiz" element={<ProtectedRoute><SolarQuiz /></ProtectedRoute>} />
        <Route path="/solarmarket" element={<SolarMarket />} />
        <Route path="/solarseminar" element={<SolarSeminar />} />



        {/* ✅ Solar Resources */}
        <Route path="/solar-resources" element={<ProtectedRoute><SolarResourcesPage /></ProtectedRoute>} />
        <Route path="/solar-resources/:id" element={<ProtectedRoute><SolarResourceDetail /></ProtectedRoute>} />

        {/* ✅ Device Error Codes */}
        <Route path="/error-codes" element={<ProtectedRoute><ErrorCodeSelector /></ProtectedRoute>} />
        <Route path="/error-codes/display" element={<ProtectedRoute><ErrorCodeDisplay /></ProtectedRoute>} />

        {/* ✅ Reusable Components */}
        <Route path="/listings" element={<ProtectedRoute><Listings /></ProtectedRoute>} />
        <Route path="/profiles" element={<ProtectedRoute><Profiles /></ProtectedRoute>} />
        <Route path="/bookings" element={<ProtectedRoute><Bookings /></ProtectedRoute>} />
        <Route path="/chat/:id" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
        <Route path="/inbox" element={<ProtectedRoute><Inbox /></ProtectedRoute>} />
        <Route path="/ai-assistant" element={<ProtectedRoute><AISolarAssistant /></ProtectedRoute>} />

        {/* ✅ NEPA/Disco Alerts */}
        <Route path="/nepa-alerts" element={<ProtectedRoute><NEPAAlerts /></ProtectedRoute>} />

        {/* ✅ Public Legal Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      </Routes>

      {/* ✅ Bottom Navigation shown conditionally */}
      {showBottomNav && <BottomNav />}
    </>
  );
}

// ===========================================
// 🧠 Global Context Wrapper
// ===========================================
function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <Router>
          <AppWrapper />
          <ToastContainer
            position="top-right"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
            closeButton={false}   // 🔥 removes the close button
            toastStyle={{
              backgroundColor: "#e9e9e9ff",   // ✅ White background
              color: "#000",             // ✅ Ensure text is visible
              borderRadius: "5px",
              padding: "12px 16px",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          />
        </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
